import { type ConfigPlugin, withAndroidManifest, withInfoPlist } from '@expo/config-plugins';

export interface LayersPluginProps {
  // iOS Configuration
  ios?: {
    // SKAdNetwork IDs to inject into Info.plist
    skAdNetworkIds?: string[];
    // Whether to include the default "usual suspects" list
    includeDefaultNetworks?: boolean;
    // Custom ATT usage description
    attUsageDescription?: string;
    // URL schemes for deep linking
    urlSchemes?: string[];
    // Associated domains for universal links
    associatedDomains?: string[];
  };

  // Android Configuration
  android?: {
    // Intent filters for deep links
    intentFilters?: Array<{
      scheme: string;
      host?: string;
    }>;
  };

  // Cross-platform
  apiKey?: string;
  appId?: string;
  enableDebug?: boolean;

  // Network-specific toggles (V1: include/exclude from defaults)
  networks?: {
    meta?: { include?: boolean };
    tiktok?: { include?: boolean };
    snap?: { include?: boolean };
    google?: { include?: boolean };
    unity?: { include?: boolean };
    applovin?: { include?: boolean };
    ironsource?: { include?: boolean };
  };
}

// Default SKAdNetwork IDs - the "usual suspects"
const DEFAULT_SKADNETWORK_IDS = [
  // Meta/Facebook
  'v9wttpbfk9.skadnetwork',
  'n38lu8286q.skadnetwork',

  // Google/YouTube
  'cstr6suwn9.skadnetwork',
  '4fzdc2evr5.skadnetwork',

  // TikTok
  '22mmun2rn5.skadnetwork',

  // Snapchat
  '424m5254lk.skadnetwork',

  // Twitter/X
  'kbd757ywx3.skadnetwork',

  // Unity Ads
  '4468km3ulz.skadnetwork',
  'v72qych5uu.skadnetwork',

  // AppLovin
  '24t9a8vw3c.skadnetwork',
  'ludvb6z3bs.skadnetwork',
  'hs6bdukanm.skadnetwork',
  'c6k4g5qg8m.skadnetwork',
  'wg4vff78zm.skadnetwork',
  '4w7y6s5ca2.skadnetwork',
  '737z793b9f.skadnetwork',
  'glqzh8vgby.skadnetwork',
  'prcb7njmu6.skadnetwork',

  // IronSource
  'su67r6k2v3.skadnetwork',
  '578prtvx9j.skadnetwork',
  '4dzt52r2t5.skadnetwork',
  'gta9lk7p23.skadnetwork',
  'r45fhb6rf7.skadnetwork',
  'xy9t38ct57.skadnetwork',
  '5a6flpkh64.skadnetwork',
  'v4nxqhlyqp.skadnetwork',
  'wzmmz9fp6w.skadnetwork',
  'yclnxrl5pm.skadnetwork',
  'mls7yz5dvl.skadnetwork',
  't38b2kh725.skadnetwork',
  '4pfyvq9l8r.skadnetwork',

  // AdMob
  'cstr6suwn9.skadnetwork',
  '4fzdc2evr5.skadnetwork',

  // Vungle
  'gta9lk7p23.skadnetwork',
  'uw77j35x4d.skadnetwork',

  // Chartboost
  'f38h382jlk.skadnetwork',
  'prcb7njmu6.skadnetwork',

  // Amazon DSP
  '7ug5zh24hu.skadnetwork',
  '9rd848q2bz.skadnetwork',

  // Pinterest
  '2u9pt9hc89.skadnetwork',
  '8s468mfl3y.skadnetwork',

  // Reddit
  '3rd42ekr43.skadnetwork',

  // Criteo
  'hs6bdukanm.skadnetwork',

  // RTB House
  '8c4e2ghe7u.skadnetwork',

  // MyTarget
  'n9x2a789qt.skadnetwork',

  // Samsung Ads
  '544jjzx7x8.skadnetwork',

  // Yahoo DSP
  '4468km3ulz.skadnetwork',
  'zq492l623r.skadnetwork'
];

const withLayers: ConfigPlugin<LayersPluginProps> = (config, props = {}) => {
  // iOS Configuration
  // eslint-disable-next-line no-param-reassign
  config = withInfoPlist(config, (config) => {
    const { ios = {} } = props || {};

    // Configure SKAdNetwork IDs
    const skAdNetworkIds = ios.skAdNetworkIds || [];
    const shouldIncludeDefaults = ios.includeDefaultNetworks !== false; // Default to true

    const allNetworkIds = [
      ...skAdNetworkIds,
      ...(shouldIncludeDefaults ? DEFAULT_SKADNETWORK_IDS : [])
    ];

    // Remove duplicates
    const uniqueNetworkIds = [...new Set(allNetworkIds)];

    if (uniqueNetworkIds.length > 0) {
      config.modResults.SKAdNetworkItems = uniqueNetworkIds.map((id) => ({
        SKAdNetworkIdentifier: id
      }));
    }

    // Configure ATT usage description
    const attDescription =
      ios.attUsageDescription ||
      'This app uses data for personalized ads and to measure ad performance.';

    config.modResults.NSUserTrackingUsageDescription = attDescription;

    // Configure URL schemes
    if (ios.urlSchemes && ios.urlSchemes.length > 0) {
      config.modResults.CFBundleURLTypes = config.modResults.CFBundleURLTypes || [];

      const layersUrlSchemes = {
        CFBundleURLName: 'LayersDeepLinks',
        CFBundleURLSchemes: ios.urlSchemes
      };

      config.modResults.CFBundleURLTypes.push(layersUrlSchemes);
    }

    // Configure associated domains for universal links
    if (ios.associatedDomains && ios.associatedDomains.length > 0) {
      config.modResults['com.apple.developer.associated-domains'] = ios.associatedDomains.map(
        (domain: string) => `applinks:${domain}`
      );
    }

    return config;
  });

  // Android Configuration
  // eslint-disable-next-line no-param-reassign
  config = withAndroidManifest(config, (config) => {
    const { android = {} } = props || {};

    // Add deep link intent filters
    if (android.intentFilters && android.intentFilters.length > 0) {
      const mainActivity = config.modResults.manifest.application?.[0]?.activity?.find(
        (activity: any) => activity.$?.['android:name'] === '.MainActivity'
      );

      if (mainActivity) {
        mainActivity['intent-filter'] = mainActivity['intent-filter'] || [];

        android.intentFilters.forEach((filter: any) => {
          const intentFilter = {
            $: {},
            action: [{ $: { 'android:name': 'android.intent.action.VIEW' } }],
            category: [
              { $: { 'android:name': 'android.intent.category.DEFAULT' } },
              { $: { 'android:name': 'android.intent.category.BROWSABLE' } }
            ],
            data: [
              {
                $: {
                  'android:scheme': filter.scheme,
                  ...(filter.host && { 'android:host': filter.host })
                }
              }
            ]
          };

          if (mainActivity['intent-filter']) {
            mainActivity['intent-filter'].push(intentFilter);
          }
        });
      }
    }

    return config;
  });

  return config;
};

export default withLayers;
